import React, { useEffect, useRef, useState } from 'react';

/**
 * CustomSelect – themed dropdown that respects dark/light mode.
 *
 * Props:
 *   value       – currently selected value
 *   onChange    – called with the new value string
 *   options     – [{ value, label }]  OR  [{ value, label, group }] for grouped lists
 *   placeholder – shown when value is empty/unmatched
 *   disabled    – disables the control
 *   style       – extra inline styles for the trigger button
 */
function CustomSelect({
  value,
  onChange,
  options = [],
  placeholder = 'Select…',
  disabled = false,
  style,
}) {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open]);

  const selectedLabel =
    options.find((o) => String(o.value) === String(value))?.label ?? placeholder;

  const handleSelect = (optionValue) => {
    if (typeof onChange === 'function') onChange(optionValue);
    setOpen(false);
  };

  return (
    <div ref={wrapperRef} style={{ position: 'relative', width: '100%', ...style }}>
      {/* Trigger */}
      <button
        type="button"
        disabled={disabled}
        onClick={() => { if (!disabled) setOpen((prev) => !prev); }}
        style={{
          ...triggerStyle,
          opacity: disabled ? 0.6 : 1,
          cursor: disabled ? 'not-allowed' : 'pointer',
        }}
      >
        <span style={triggerTextStyle}>{selectedLabel}</span>
        <svg
          width="14"
          height="14"
          viewBox="0 0 14 14"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          style={{
            flexShrink: 0,
            opacity: 0.6,
            transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.18s ease',
          }}
        >
          <polyline points="2 5 7 10 12 5" />
        </svg>
      </button>

      {/* Dropdown */}
      {open && (
        <div style={dropdownStyle}>
          {options.map((opt) => {
            const isSelected = String(opt.value) === String(value);
            return (
              <button
                key={opt.value}
                type="button"
                onMouseDown={() => handleSelect(opt.value)}
                style={{
                  ...itemStyle,
                  ...(isSelected ? selectedItemStyle : null),
                }}
              >
                {isSelected && <span style={checkStyle}>✓</span>}
                <span style={isSelected ? selectedItemTextStyle : undefined}>
                  {opt.label}
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

const triggerStyle = {
  width: '100%',
  minHeight: '48px',
  padding: '12px 14px',
  borderRadius: '12px',
  border: '1px solid var(--border)',
  background: 'var(--input-bg)',
  color: 'var(--text-primary)',
  fontSize: '14px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '10px',
  textAlign: 'left',
};

const triggerTextStyle = {
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  flex: 1,
};

const dropdownStyle = {
  position: 'absolute',
  top: 'calc(100% + 6px)',
  left: 0,
  right: 0,
  zIndex: 200,
  background: 'var(--popup-bg)',
  border: '1px solid var(--popup-border)',
  borderRadius: '14px',
  boxShadow: '0 8px 32px rgba(0,0,0,0.45)',
  overflow: 'hidden',
  maxHeight: '320px',
  overflowY: 'auto',
};

const itemStyle = {
  width: '100%',
  padding: '11px 14px',
  border: 'none',
  borderBottom: '1px solid var(--popup-item-border)',
  background: 'var(--popup-item-bg)',
  color: 'var(--popup-text)',
  fontSize: '14px',
  fontWeight: 500,
  cursor: 'pointer',
  textAlign: 'left',
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
};

const selectedItemStyle = {
  background: 'var(--primary)',
  borderBottomColor: 'transparent',
  color: '#ffffff',
  fontWeight: 700,
};

const selectedItemTextStyle = {
  color: '#ffffff',
};

const checkStyle = {
  fontSize: '12px',
  flexShrink: 0,
};

export default CustomSelect;
